import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, y = 24, className = "", ...rest }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div className={className}>{children}</div>;
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            {...rest}
        >
            {children}
        </motion.div>
    );
};

export const Eyebrow = ({ index, children, dark = false }) => (
    <div
        className={`flex items-center gap-3 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.22em] ${
            dark ? "text-vec-green-bright" : "text-vec-green"
        }`}
    >
        {index && <span className={dark ? "text-white/40" : "text-vec-charcoal/40"}>{index}</span>}
        <span className={`h-px w-10 ${dark ? "bg-vec-green-bright/60" : "bg-vec-green/50"}`} aria-hidden="true" />
        <span>{children}</span>
    </div>
);

export const SectionHead = ({ index, eyebrow, title, sub, dark = false, align = "left" }) => (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
        <Reveal>
            <Eyebrow index={index} dark={dark}>
                {eyebrow}
            </Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
            <h2
                className={`mt-5 font-display text-[clamp(1.5rem,1.05rem+1.5vw,2.75rem)] font-semibold tracking-tight leading-[1.12] uppercase text-balance ${
                    dark ? "text-white" : "text-vec-charcoal"
                }`}
            >
                {title}
            </h2>
        </Reveal>
        {sub && (
            <Reveal delay={0.16}>
                <p className={`mt-5 text-base sm:text-lg leading-relaxed ${dark ? "text-white/70" : "text-vec-charcoal/70"}`}>
                    {sub}
                </p>
            </Reveal>
        )}
    </div>
);

export const Counter = ({ value, prefix = "", suffix = "", plain = false, duration = 1200 }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const reduce = useReducedMotion();
    const [display, setDisplay] = useState(plain || reduce ? value : 0);

    useEffect(() => {
        if (!inView || plain || reduce) return;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(eased * value));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value, plain, reduce, duration]);

    return (
        <span ref={ref}>
            {prefix}
            {display}
            {suffix}
        </span>
    );
};

export const ArrowLink = ({ children, dark = false }) => (
    <span
        className={`inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
            dark ? "text-vec-green-bright" : "text-vec-green group-hover:text-vec-green-bright"
        }`}
    >
        {children}
        <ArrowRight
            size={14}
            className="transition-transform duration-300 group-hover:translate-x-1"
            aria-hidden="true"
        />
    </span>
);
